'use client';

import {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useTransition,
} from 'react';
import {
  checkCanPerformBulkAction,
  CopyCommentsOptions,
  useCopyAssetsWithWarning,
  useMoveAssetsWithWarning,
} from '@frameio/assets/operations';
import { useSelectionForOperations } from '@frameio/assets/selection';
import type { AssetParentFragment } from '@frameio/assets/shared/gql/generated/AssetParent';
import { useGetProjectForNavigationQuery } from '@frameio/graphql-client/generated';
import { useClearFocusedAsset } from '@frameio/selection';
import { ClientRenderBoundary } from '@frameio/shared/components/ClientRenderBoundary';
import type { MoveToCopyToStatus } from '@frameio/shared/contexts';
import {
  useActiveAccountId,
  useActiveFolderId,
  useActiveProjectId,
  useDispatchAppState,
  useMoveToCopyToStatus,
} from '@frameio/shared/contexts';
import { useCurrentPageType } from '@frameio/shared/routing/getPageType';
import {
  DrillDown,
  usePanelTransitionStatus,
  usePrevious,
} from '@frameio/vapor';
import {
  AccountProjectsView,
  FoldersView,
  MoveToCopyToSheet,
  moveType,
} from './components';
import type { MoveToCopyToProject } from './components/types';
import TEXT from './generated/TEXT/MoveToCopyTo';

TEXT.DEFINE({
  /** Back button to the list of projects */
  backLabel: 'Go back',
});

function MoveToCopyTo() {
  const accountId = useActiveAccountId();
  const activeProjectId = useActiveProjectId();
  const prevActiveProjectId = usePrevious(activeProjectId);
  const [isViewChanging, startTransition] = useTransition();

  const sourceId = useActiveFolderId();

  const [targetProject, setTargetProject] =
    useState<MoveToCopyToProject | null>();
  const [currentView, setCurrentView] = useState<View>(
    activeProjectId ? View.FOLDERS_VIEW : View.PROJECTS_VIEW
  );

  const [copyComments, setCopyComments] = useState(false);
  const [targetFolderId, setTargetFolderId] = useState<string | null>(
    useActiveFolderId()
  );
  const [dropdownValue, setDropdownValue] = useState<CopyCommentsOptions>(
    CopyCommentsOptions.ALL
  );

  /**
   * Fetch the project and workspace it belongs to to default to the Folders view
   */
  const { data: projectResult, loading: projectDataLoading } =
    useGetProjectForNavigationQuery({
      variables: {
        projectId: activeProjectId,
      },
    });
  const projectData = projectResult?.account?.project;

  const moveToCopyToStatus = useMoveToCopyToStatus();
  const { mountTransitionComplete } = usePanelTransitionStatus();
  const previousStatus = usePrevious(moveToCopyToStatus);
  const isShowing = moveToCopyToStatus !== 'hidden' && mountTransitionComplete;

  const changingWorkspaces =
    targetProject &&
    projectData?.workspace?.id !== targetProject?.workspace?.id;

  const movePrivateCommentsToNewWorkspace = changingWorkspaces
    ? dropdownValue === CopyCommentsOptions.ALL
    : undefined;

  const dispatchAppState = useDispatchAppState();
  const { selectedIds } = useSelectionForOperations(false);
  const clearFocusedAssetId = useClearFocusedAsset();
  const selectedCount = selectedIds.length;

  const copyAssets = useCopyAssetsWithWarning();
  const moveAssets = useMoveAssetsWithWarning();

  const projectId = targetProject?.id;

  const pageType = useCurrentPageType();

  let canSubmit =
    targetProject !== null && selectedCount > 0 && targetFolderId !== null;

  if (moveToCopyToStatus === 'move') {
    canSubmit =
      canSubmit &&
      targetFolderId !== sourceId &&
      // don't allow moving a folder into itself
      !selectedIds.includes(targetFolderId ?? '');
  }

  // Create a key lookup + handle if status == hidden use previous status.
  const methodOptions: { [status in MoveToCopyToStatus]: moveType } = {
    copy: moveType.COPY_TO,
    move: moveType.MOVE_TO,
    hidden: previousStatus === 'copy' ? moveType.COPY_TO : moveType.MOVE_TO,
  };

  const method: moveType = methodOptions[moveToCopyToStatus];
  const prevCountWhenActive = useRef(selectedCount);

  const handleFolderSelect = (folderId: string) => {
    setTargetFolderId(folderId);
  };

  const handleRequestClose = useCallback(() => {
    clearFocusedAssetId();
    dispatchAppState({
      payload: 'hidden',
      type: 'MOVE_TO_COPY_TO_STATUS_CHANGE',
    });
  }, [dispatchAppState, clearFocusedAssetId]);

  /**
   * this useEffect handles the closing of the sheet when moving to a different
   * project outside of the sheet. when changing the project within the sheet,
   * either the previous project or the next project will always be undefined.
   */
  useEffect(() => {
    if (!prevActiveProjectId || !activeProjectId) return;

    if (prevActiveProjectId !== activeProjectId) {
      handleRequestClose();
    }
  }, [activeProjectId, prevActiveProjectId, handleRequestClose]);

  /**
   * this effect sets the current workspace and project
   *
   */
  useEffect(() => {
    if (!projectDataLoading && !targetProject && projectData?.id) {
      setTargetProject(projectData as unknown as MoveToCopyToProject);
    }
  }, [projectData, projectDataLoading, targetProject]);

  /**
   * this effect makes sure that the AppState is updated whenever this
   * component gets unmounted (the sheet state should be 'hidden')
   */
  useEffect(() => handleRequestClose, [handleRequestClose]);

  /*
   * On a move the selected Ids will reset and become 0.
   * Retain a previous count reflective of what the sheet is moving.
   * Count is shown when sheet is animating out during "hidden"
   * and selected Ids is 0.
   * Similar to a usePrevious() but avoid any update on hidden.
   */
  useEffect(() => {
    if (moveToCopyToStatus !== 'hidden')
      prevCountWhenActive.current = selectedCount;
    setDropdownValue(CopyCommentsOptions.ALL);
    setCopyComments(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [moveToCopyToStatus, selectedIds]);

  // This useEffect causes the move to copy to sheet to close when navigating to a share.
  useEffect(() => {
    if (pageType === 'shareBuilder') {
      handleRequestClose();
    }
  }, [handleRequestClose, pageType]);

  const handleSubmit = () => {
    const destination: AssetParentFragment = {
      id: targetFolderId ?? '',
      project: {
        id: projectId ?? '',
      },
    };

    const assetIds = Array.from(selectedIds).filter((id) => id !== null);
    const copyCommentsOption = copyComments
      ? CopyCommentsOptions[dropdownValue]
      : CopyCommentsOptions.NONE;
    if (moveToCopyToStatus === 'copy') {
      /**
       * When copying to a destination folder
       * that is the same as the source folder,
       * we call duplicateAssets ensuring:
       * - The duplicated assets is suffixed with an incrementing number (x)
       * - The displayed toast does not display a "Go to folder" link
       */
      copyAssets({
        assetIds,
        copyComments: copyCommentsOption,
        destination,
        duplicate: sourceId === destination.id,
      });
    } else {
      moveAssets({
        assetIds,
        changingWorkspaces: Boolean(changingWorkspaces),
        destination,
        sourceId,
        movePrivateCommentsToNewWorkspace,
      });
    }

    handleRequestClose();
  };

  const projects = useMemo(
    () => (
      <AccountProjectsView
        accountId={accountId}
        isShowing={isShowing}
        onWorkspaceProjectSelect={({ project, workspace }) => {
          startTransition(() => {
            setTargetProject({ ...project, workspace });
            setTargetFolderId(project.rootAssetId);
            setCurrentView(View.FOLDERS_VIEW);
          });
        }}
      />
    ),
    [accountId, isShowing]
  );

  const folders = useMemo(
    () =>
      isViewChanging ? null : (
        <FoldersView
          onBackSelect={() => {
            setTargetProject(null);
            setCurrentView(View.PROJECTS_VIEW);
            setTargetFolderId(null);
          }}
          onFolderSelect={handleFolderSelect}
          project={targetProject ?? undefined}
          targetFolderId={targetFolderId ?? undefined}
          workspace={targetProject?.workspace ?? undefined}
        />
      ),
    [isViewChanging, targetProject, targetFolderId]
  );

  const selectedCountWhenActive =
    moveToCopyToStatus === 'hidden'
      ? prevCountWhenActive.current
      : selectedCount;

  const canPerformBulkAction = checkCanPerformBulkAction({
    action: method === moveType.MOVE_TO ? 'move' : 'copy',
    assetCount: selectedCount,
  });

  return (
    <MoveToCopyToSheet
      canSubmit={canSubmit && canPerformBulkAction}
      changingWorkspaces={Boolean(changingWorkspaces)}
      copyComments={copyComments}
      dropdownValue={dropdownValue}
      enableTooltip={!canPerformBulkAction}
      isShowing={isShowing}
      method={method}
      onCopyCommentsChange={(value) => setCopyComments(value)}
      onDropdownSelection={setDropdownValue}
      onRequestClose={handleRequestClose}
      onSubmit={handleSubmit}
      selectedCount={selectedCountWhenActive}
    >
      <DrillDown
        backLabel={TEXT.backLabel}
        columnBackgroundColor="primary"
        columns={[
          {
            content: projects,
          },
          {
            content: folders,
          },
        ]}
        disableColumnsTitle={true}
        onChange={(i) => setCurrentView(i)}
        value={currentView}
      />
    </MoveToCopyToSheet>
  );
}

/**
 * Encapsulate MoveTo inside a client boundary. Because it is a sheet, we only
 * want to render on the client after user interaction. This allows us to
 * prevent MoveToCopyTo from ever rendering on the server
 */
const MoveToCopyToClientOnly = () => (
  <ClientRenderBoundary>
    <MoveToCopyTo />
  </ClientRenderBoundary>
);
const MoveToCopyToMemo = memo(MoveToCopyToClientOnly);
export { MoveToCopyToMemo as MoveToCopyTo };

enum View {
  PROJECTS_VIEW = 0,
  FOLDERS_VIEW,
}
